import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, ArrowLeft, Shield, Users, IndianRupee, FileText, AlertTriangle, Scale } from 'lucide-react';
import { ENV } from '../config/env';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="pt-20">
      <header className="pf-section-hero py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6" />
            </div>
            <h1 className="text-4xl font-bold">Terms and Conditions</h1>
          </div>
          <p className="text-white/80 text-lg">
            Last updated: November 11, 2025
          </p>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          
          {/* Introduction */}
          <section className="mb-10" data-pf-reveal="left">
            <p className="text-gray-700 text-lg leading-relaxed mb-4">
              Welcome to <strong>PetsFostering</strong> ("we," "our," or "us"). These Terms and Conditions ("Terms") govern your access to and use of our platform, including our website, mobile applications, and all related services (collectively, the "Platform").
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              By accessing or using PetsFostering, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Platform.
            </p>
          </section>

          {/* 1. Platform Overview */}
          <section className="mb-10" data-pf-reveal="left">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--pf-accent-soft)] rounded-lg flex items-center justify-center">
                <Heart className="w-5 h-5 text-[var(--pf-accent)]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">1. Platform Overview</h2>
            </div>
            <div className="space-y-4 text-gray-700 pl-13">
              <p>
                PetsFostering is a connection platform that helps pet owners ("Owners") find independent pet care service providers ("Caregivers"). We facilitate connections between users but do not provide pet care services directly.
              </p>
              <p>
                <strong>Our Role:</strong> We operate similarly to platforms like Ola or Uber. We connect people who need services with people who provide services. We do not employ caregivers, and we are not responsible for the actual delivery of pet care services.
              </p>
              <p>
                <strong>Platform Fee Model:</strong> We charge a platform usage fee (currently 10% of booking value) for facilitating connections and providing our technology platform. Additional subscription fees may apply for premium features like Offline Jobs Tracking.
              </p>
            </div>
          </section>

          {/* 2. User Accounts */}
          <section className="mb-10" data-pf-reveal="left">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">2. User Accounts and Registration</h2>
            </div>
            <div className="space-y-4 text-gray-700 pl-13">
              <p><strong>2.1 Eligibility:</strong> You must be at least 18 years old to create an account and use our Platform.</p>
              
              <p><strong>2.2 Account Types:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Pet Owners:</strong> Individuals seeking pet care services for their pets</li>
                <li><strong>Caregivers:</strong> Independent service providers offering pet care services</li>
                <li><strong>Administrators:</strong> Platform management accounts with special privileges</li>
              </ul>

              <p><strong>2.3 Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials. You agree to notify us immediately of any unauthorized access or security breach.</p>

              <p><strong>2.4 Accurate Information:</strong> You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.</p>

              <p><strong>2.5 Self-Registration:</strong> Users may create accounts independently or accounts may be created by administrators. All accounts are subject to these Terms regardless of creation method.</p>
            </div>
          </section>

          {/* 3. Services and Features */}
          <section className="mb-10" data-pf-reveal="left">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">3. Services and Features</h2>
            </div>
            <div className="space-y-4 text-gray-700 pl-13">
              <p><strong>3.1 Core Platform Services (Free):</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Profile creation and management</li>
                <li>Caregiver search and discovery</li>
                <li>Basic messaging between users</li>
                <li>Booking management</li>
                <li>Reviews and ratings</li>
              </ul>

              <p><strong>3.2 Premium Features (Subscription Required):</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Offline Jobs Tracking (₹79/month):</strong> Track external bookings, manage receipts, export reports, and analyze complete business performance</li>
                <li>Additional premium features may be added in the future</li>
              </ul>

              <p><strong>3.3 Coupon System:</strong> We may offer promotional coupons for free trials or discounts. Coupon terms are specified at the time of redemption and may include expiration dates, usage limits, and specific feature access.</p>
            </div>
          </section>

          {/* 4. Payment Terms */}
          <section className="mb-10" data-pf-reveal="left">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">4. Payment Terms</h2>
            </div>
            <div className="space-y-4 text-gray-700 pl-13">
              <p><strong>4.1 Direct Payment Between Users:</strong> Pet owners pay caregivers directly for services rendered. PetsFostering does not handle, process, or escrow payments between owners and caregivers.</p>

              <p><strong>4.2 Platform Fees:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Usage Fee:</strong> We charge owners a platform fee (currently 10% of booking value) for facilitating connections</li>
                <li><strong>Subscription Fees:</strong> Premium features require monthly subscription payments (e.g., Offline Jobs Pro at ₹79/month)</li>
                <li>All platform fees are non-refundable unless otherwise specified</li>
              </ul>

              <p><strong>4.3 Payment Disputes:</strong> Any disputes regarding payments between owners and caregivers are to be resolved directly between the parties. PetsFostering is not responsible for resolving payment disputes or mediating financial disagreements.</p>

              <p><strong>4.4 Taxes:</strong> Users are responsible for determining and paying any applicable taxes related to their use of the Platform or services provided/received.</p>
            </div>
          </section>

          {/* 5. User Responsibilities */}
          <section className="mb-10" data-pf-reveal="left">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">5. User Responsibilities</h2>
            </div>
            <div className="space-y-4 text-gray-700 pl-13">
              <p><strong>5.1 For Pet Owners:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide accurate information about your pets, including health conditions, behavior, and special needs</li>
                <li>Ensure your pets are properly vaccinated and in good health</li>
                <li>Communicate clearly with caregivers about expectations and requirements</li>
                <li>Pay caregivers promptly for services rendered</li>
                <li>Treat caregivers with respect and professionalism</li>
              </ul>

              <p><strong>5.2 For Caregivers:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide accurate information about your experience, qualifications, and availability</li>
                <li>Deliver services professionally and as agreed upon with owners</li>
                <li>Ensure the safety and well-being of pets in your care</li>
                <li>Maintain appropriate insurance and licenses as required by local laws</li>
                <li>Communicate promptly with owners about any issues or concerns</li>
                <li>Be honest about your capabilities and limitations</li>
              </ul>

              <p><strong>5.3 Prohibited Activities:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Posting false, misleading, or fraudulent information</li>
                <li>Harassment, abuse, or threatening behavior toward other users</li>
                <li>Using the Platform for any illegal purpose</li>
                <li>Attempting to circumvent platform fees by arranging services outside the Platform after initial connection</li>
                <li>Sharing account credentials or accessing another user's account</li>
                <li>Scraping, data mining, or automated data collection</li>
                <li>Posting inappropriate, offensive, or harmful content</li>
              </ul>
            </div>
          </section>

          {/* 6. Liability and Disclaimers */}
          <section className="mb-10" data-pf-reveal="left">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--pf-accent-soft)] rounded-lg flex items-center justify-center">
                <Scale className="w-5 h-5 text-[var(--pf-accent)]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">6. Liability and Disclaimers</h2>
            </div>
            <div className="space-y-4 text-gray-700 pl-13">
              <p><strong>6.1 Platform as Intermediary:</strong> PetsFostering is a connection platform only. We do not provide pet care services, employ caregivers, or guarantee the quality of services provided by caregivers.</p>

              <p><strong>6.2 No Warranty:</strong> The Platform is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.</p>

              <p><strong>6.3 Limitation of Liability:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>We are not responsible for the actions, conduct, or services of caregivers or owners</li>
                <li>We are not liable for any injury, harm, loss, or damage to pets, persons, or property</li>
                <li>We are not responsible for disputes between users</li>
                <li>Our total liability shall not exceed the amount paid by you to us in the 12 months preceding any claim</li>
              </ul>

              <p><strong>6.4 User Verification:</strong> While we may provide tools for user profiles and reviews, we do not conduct comprehensive background checks or verify all information provided by users. Users are responsible for their own due diligence.</p>

              <p><strong>6.5 Insurance:</strong> Caregivers are solely responsible for obtaining appropriate insurance coverage. PetsFostering does not provide insurance coverage for services or incidents.</p>
            </div>
          </section>

          {/* 7. Intellectual Property */}
          <section className="mb-10" data-pf-reveal="left">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Intellectual Property</h2>
            <div className="space-y-4 text-gray-700">
              <p><strong>7.1 Platform Content:</strong> All content on the Platform, including but not limited to text, graphics, logos, icons, images, audio clips, and software, is the property of PetsFostering or its licensors and is protected by copyright and other intellectual property laws.</p>

              <p><strong>7.2 User Content:</strong> You retain ownership of content you post on the Platform (profiles, reviews, messages, etc.). By posting content, you grant us a non-exclusive, worldwide, royalty-free license to use, display, and distribute your content on the Platform.</p>

              <p><strong>7.3 Trademarks:</strong> "PetsFostering," our logo, and other marks are trademarks of our company. You may not use our trademarks without our prior written consent.</p>
            </div>
          </section>

          {/* 8. Privacy and Data */}
          <section className="mb-10" data-pf-reveal="left">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Privacy and Data Protection</h2>
            <div className="space-y-4 text-gray-700">
              <p><strong>8.1 Privacy Policy:</strong> Our collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference.</p>

              <p><strong>8.2 Data Security:</strong> We implement reasonable security measures to protect your data. However, no method of transmission over the internet is 100% secure.</p>

              <p><strong>8.3 User Communications:</strong> Messages and communications between users through the Platform may be monitored for quality, safety, and compliance purposes.</p>
            </div>
          </section>

          {/* 9. Termination */}
          <section className="mb-10" data-pf-reveal="left">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Account Termination</h2>
            <div className="space-y-4 text-gray-700">
              <p><strong>9.1 By You:</strong> You may terminate your account at any time by contacting us or using account deletion features in the Platform.</p>

              <p><strong>9.2 By Us:</strong> We reserve the right to suspend or terminate your account if you violate these Terms, engage in prohibited activities, or for any other reason at our sole discretion.</p>

              <p><strong>9.3 Effect of Termination:</strong> Upon termination, your right to use the Platform ceases immediately. Provisions that by their nature should survive termination (including liability limitations and dispute resolution) will continue to apply.</p>
            </div>
          </section>

          {/* 10. Dispute Resolution */}
          <section className="mb-10" data-pf-reveal="left">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Dispute Resolution</h2>
            <div className="space-y-4 text-gray-700">
              <p><strong>10.1 Governing Law:</strong> These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions.</p>

              <p><strong>10.2 Jurisdiction:</strong> Any disputes arising from these Terms or your use of the Platform shall be subject to the exclusive jurisdiction of the courts located in [Your City], India.</p>

              <p><strong>10.3 Arbitration:</strong> Before filing any lawsuit, the parties agree to attempt to resolve disputes through good faith negotiation for a period of 30 days.</p>
            </div>
          </section>

          {/* 11. Changes to Terms */}
          <section className="mb-10" data-pf-reveal="left">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Modifications to Terms</h2>
            <div className="space-y-4 text-gray-700">
              <p>We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting on the Platform. Your continued use of the Platform after changes are posted constitutes your acceptance of the modified Terms.</p>
              <p>We will notify users of material changes via email or prominent notice on the Platform.</p>
            </div>
          </section>

          {/* 12. General Provisions */}
          <section className="mb-10" data-pf-reveal="left">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. General Provisions</h2>
            <div className="space-y-4 text-gray-700">
              <p><strong>12.1 Entire Agreement:</strong> These Terms, together with our Privacy Policy, constitute the entire agreement between you and PetsFostering.</p>

              <p><strong>12.2 Severability:</strong> If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.</p>

              <p><strong>12.3 Waiver:</strong> Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.</p>

              <p><strong>12.4 Assignment:</strong> You may not assign or transfer these Terms or your account without our prior written consent. We may assign these Terms without restriction.</p>

              <p><strong>12.5 Force Majeure:</strong> We shall not be liable for any failure to perform due to circumstances beyond our reasonable control, including natural disasters, war, terrorism, pandemics, or internet service failures.</p>
            </div>
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <div className="pf-organic-card bg-[var(--pf-accent-soft)] p-8 border-[color-mix(in_srgb,var(--pf-accent)_20%,var(--pf-border))]">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms and Conditions, please contact us:
              </p>
              <div className="space-y-2 text-gray-700">
                <p><strong>Email:</strong> legal@PetsFostering.com</p>
                <p><strong>Support:</strong> support@PetsFostering.com</p>
                <p><strong>Website:</strong> <a href={ENV.APP.URL} className="text-[var(--pf-accent)] hover:underline">{ENV.APP.DOMAIN}</a></p>
              </div>
            </div>
          </section>

          {/* Acknowledgment */}
          <section>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg">
              <p className="text-gray-800 font-semibold mb-2">
                ⚠️ Important Notice
              </p>
              <p className="text-gray-700">
                By creating an account or using PetsFostering, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree to these Terms, you must not use our Platform.
              </p>
            </div>
          </section>

        </div>

        {/* Back to Top */}
        <div className="text-center mt-8">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-[var(--pf-accent)] hover:underline font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Terms;

